import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Spinner, House, ArrowRight, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { BUSINESS } from "@/data/site";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  // Support both Stripe (session_id) and Square (ji_ref) redirect param names.
  const sessionId = params.get("session_id") || params.get("ji_ref");
  const [state, setState] = useState({ status: "checking", data: null, error: null });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  const stop = useCallback(() => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } }, []);

  const poll = useCallback(async () => {
    if (!sessionId) { setState({ status: "error", error: "Missing session_id", data: null }); return; }
    if (attemptsRef.current >= MAX_ATTEMPTS) {
      setState({ status: "timeout", error: "Payment check timed out. If you were charged, please contact us.", data: null });
      return;
    }
    attemptsRef.current += 1;
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid" || data.status === "complete") {
        setState({ status: "paid", data, error: null }); return;
      }
      if (["expired", "failed", "cancelled"].includes(data.status)) {
        setState({ status: "failed", data, error: `Payment ${data.status}.` }); return;
      }
      setState({ status: "checking", data, error: null });
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (err) {
      setState({ status: "error", error: err?.response?.data?.detail || "Could not check payment status.", data: null });
    }
  }, [sessionId]);

  useEffect(() => { poll(); return stop; }, [sessionId, poll, stop]);

  const isPaid = state.status === "paid";
  const isChecking = state.status === "checking";
  const isFailed = state.status === "failed" || state.status === "timeout" || state.status === "error";

  return (
    <div data-testid="payment-success-page" className="ji-container py-24 md:py-32">
      <div className="max-w-2xl mx-auto text-center">
        {isChecking && (
          <div data-testid="payment-checking">
            <Spinner size={44} className="animate-spin mx-auto text-[var(--ji-primary)]" />
            <h1 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-6 text-4xl md:text-5xl font-bold tracking-tight">Confirming your payment…</h1>
            <p className="mt-3 text-[var(--ji-muted)]">Hang tight — PayPal usually confirms in a few seconds.</p>
          </div>
        )}
        {isPaid && (
          <div data-testid="payment-paid">
            <div className="inline-flex w-20 h-20 items-center justify-center bg-emerald-100 text-emerald-700" style={{ borderRadius: 999 }}>
              <CheckCircle size={44} weight="fill" />
            </div>
            <h1 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-6 text-4xl md:text-5xl font-bold tracking-tight">Diagnostic booked.</h1>
            <p className="mt-3 text-[var(--ji-muted)] max-w-lg mx-auto leading-relaxed">
              Thanks for booking with Joseph Industries. Your diagnostic visit is scheduled and David will confirm the time window shortly.
              A receipt has been emailed to you.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" data-testid="payment-home-link" className="ji-btn-primary inline-flex items-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                <House size={16} weight="bold" /> Back to home
              </Link>
              <a href={`tel:${BUSINESS.primaryPhone.tel}`} data-testid="payment-call-link" className="ji-btn-outline inline-flex items-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                Call David
              </a>
            </div>
          </div>
        )}
        {isFailed && (
          <div data-testid="payment-failed">
            <div className="inline-flex w-20 h-20 items-center justify-center bg-red-100 text-red-700" style={{ borderRadius: 999 }}>
              {state.status === "timeout" ? <Warning size={44} weight="fill" /> : <XCircle size={44} weight="fill" />}
            </div>
            <h1 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-6 text-4xl md:text-5xl font-bold tracking-tight">
              {state.status === "timeout" ? "Still processing…" : "Something didn't go through."}
            </h1>
            <p className="mt-3 text-[var(--ji-muted)] max-w-lg mx-auto leading-relaxed">
              {state.error || "Please try again or give us a call — if your card was charged, we'll sort it out."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/diagnostic" data-testid="payment-retry-link" className="ji-btn-primary inline-flex items-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                Try again <ArrowRight size={16} weight="bold" />
              </Link>
              <a href={`tel:${BUSINESS.primaryPhone.tel}`} className="ji-btn-outline inline-flex items-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                Call {BUSINESS.primaryPhone.display}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
